/**
 * Floating bubbles: our Google rating badge (snippets/cfs-review-badge.liquid) and the Shopify Inbox
 * chat button.
 *
 * Both sit in the bottom corners of the screen. This module never touches what Inbox renders and never
 * moves its DOM. It only measures, then hands numbers to CSS (assets/cfs-global.css, floating part):
 *   - level: the badge's centre is lined up with the chat button's centre, on every page and screen size
 *   - lift: both rise above the product page's sticky add bar and above the footer's bottom line
 *     (copyright / payment marks), so they never cover either
 *   - hide: on the cart page both are hidden while the checkout summary (desktop: the Checkout button) sits in their
 *     corner, and on the mobile product page while the quantity / Add to basket row (and the "Pay
 *     securely" line under it, or a sold-out product's back-in-stock box) is in their corner, e.g. before the
 *     shopper scrolls (owner, 9 Oct 2026; sold out: 25 Oct 2026)
 *     (class cfs-float-hidden on <html>)
 *
 * How each is moved:
 *   - Chat: Inbox's own public variable, --shopify-chat-activator-offset, set on the <shopify-chat> host.
 *   - Badge: --cfs-badge-bottom on the badge (its `bottom`; its popover inherits it to open above it).
 *     Until 14 Oct 2026 this was Google Merchant Center's widget, moved with `translate`.
 * Both variables are written on those two elements, not on <html>: the lift changes on every scroll frame
 * near the footer, and a custom property changed on <html> re-computes the style of the whole page.
 *
 * Contract with the rest of the theme:
 *   - the product page's sticky add bar is [data-cfs-sticky-add]; it must have no height while it is
 *     not shown (hidden or display: none). Its real height is measured here, never hard-coded.
 *   - the footer's bottom line is [data-cfs-footer-line] (falls back to .cfs-footer__bottom): the row whose
 *     top edge is the line above the copyright and payment marks. The 72px above it stays empty, so the
 *     bubbles can rest there between the Visit block and the line.
 */

const CHAT_HEIGHT = 50; // Inbox button at its 16px font: 46px + (16px - 13px) * 1.4
const GAP = 12; // clear space kept between a bubble and the sticky bar or footer line
const ZONE = 80; // height of the bottom corner a sticky checkout must clear before the bubbles may show

const root = document.documentElement;
const narrow = window.matchMedia('(max-width: 989px)');

// Inbox's resting offset comes from its embed's :root rule ("Button vertical position" setting). Read on
// the first update, not while the module loads: styles are still settling then, and reading them forced
// an early style recalculation (Lighthouse "Forced reflow", owner 11 Oct 2026).
let rest = 0;
function readRest() {
  const value = parseFloat(getComputedStyle(root).getPropertyValue('--shopify-chat-activator-offset'));
  rest = value > 0 ? value : 20;
}

/** @type {HTMLElement | null} */
let chat = null;
/** @type {HTMLElement | null} */
let bar = null;
/** @type {HTMLElement | null} */
let footerLine = null;
/** @type {HTMLElement | null} */
let badge = null;
let onCartPage = false;
/** @type {HTMLElement | null} the product page's own quantity / Add to basket row */
let buyRow = null;
/** @type {HTMLElement | null} the "Pay securely with" line under it */
let payLine = null;
// From 750px up the page scrolls inside .page-wrapper (html and body are overflow: hidden); below that the window scrolls.
/** @type {Element | null} */
let pageWrapper = null;

let pillHeight = 0;
let queued = false;
let measureDue = true;

const observer = new ResizeObserver(() => queue());

/** Writes a custom property on an element only when its value changes (null removes it). */
function setVar(/** @type {HTMLElement | null} */ element, /** @type {string} */ name, /** @type {string | null} */ value) {
  if (!element || element.style.getPropertyValue(name) === (value ?? '')) return;
  if (value === null) element.style.removeProperty(name);
  else element.style.setProperty(name, value);
}

function update() {
  queued = false;
  if (!rest) readRest();
  if (measureDue) {
    measureDue = false;
    pillHeight = badge?.firstElementChild instanceof HTMLElement ? badge.firstElementChild.offsetHeight : 0;
  }

  const viewport = window.innerHeight;
  let lift = 0;
  if (bar) {
    const box = bar.getBoundingClientRect();
    if (box.height) lift = Math.max(lift, viewport - box.top + GAP - rest); // 0 height = bar not shown
  }
  if (footerLine) {
    const box = footerLine.getBoundingClientRect();
    if ((box.height || box.width) && box.top < viewport) lift = Math.max(lift, viewport - box.top + GAP - rest);
  }
  lift = Math.max(0, Math.round(lift));

  let hidden = false;
  if (onCartPage && narrow.matches) {
    const box = document.querySelector('.cfs-cart-page .cfs-basket__summary')?.getBoundingClientRect();
    hidden = Boolean(box && box.height && box.bottom > viewport - ZONE && box.top < viewport);
  } else if (onCartPage) {
    // Desktop: the summary column starts beside the heading, so Checkout can sit in the chat's corner
    const box = document.querySelector('.cfs-cart-page [data-cfs-checkout]')?.getBoundingClientRect();
    hidden = Boolean(box && box.height && box.bottom > viewport - ZONE && box.top < viewport);
  }
  if (!hidden && buyRow && narrow.matches) {
    const row = buyRow.getBoundingClientRect();
    const bottom = Math.max(row.bottom, payLine?.getBoundingClientRect().bottom ?? 0);
    hidden = Boolean(row.height && bottom > viewport - ZONE - lift && row.top < viewport - lift);
  }

  setVar(chat, '--shopify-chat-activator-offset', lift ? `${rest + lift}px` : null);
  if (pillHeight) setVar(badge, '--cfs-badge-bottom', `${Math.round(rest + (CHAT_HEIGHT - pillHeight) / 2 + lift)}px`);
  root.classList.toggle('cfs-float-hidden', hidden);
  // A hidden badge closes its popover, so it never floats on its own over the checkout row
  if (hidden) badge?.querySelector(':popover-open')?.hidePopover?.();
}

/**
 * Schedules one update. Scrolling passes false: nothing about the badge itself changes, and scrolling
 * leaves the layout clean, so it runs on the next frame. Anything else (the chat app adding or resizing
 * its bubble, content changing size) runs just after the next frame is drawn, when the layout is up to
 * date, so measuring doesn't force the browser to lay the page out early (Lighthouse "Forced reflow").
 */
function queue(measure = true) {
  if (measure) measureDue = true;
  if (queued) return;
  queued = true;
  if (measure) requestAnimationFrame(() => setTimeout(update, 0));
  else requestAnimationFrame(update);
}

/** Finds the chat, badge, sticky bar, footer line and cart page, and watches the ones that can change size. */
function connect() {
  chat = document.querySelector('shopify-chat');
  badge = document.getElementById('cfs-review-badge');
  bar = document.querySelector('[data-cfs-sticky-add]');
  footerLine = document.querySelector('[data-cfs-footer-line], .cfs-footer__bottom');
  onCartPage = Boolean(document.querySelector('.cfs-cart-page'));
  // Sold out: the back-in-stock box takes the buy row's place, so the bubbles keep off it too (owner, 25 Oct 2026)
  buyRow = document.querySelector('[data-cfs-main-actions]') ?? document.querySelector('.cfs-product [data-cfs-restock]');
  payLine = document.querySelector('.cfs-product__pay');
  pageWrapper = document.querySelector('.page-wrapper');

  // Content growing or shrinking moves the footer and the cart summary without a scroll event. On desktop
  // the body is a fixed 100% tall, so watch the main content and the footer line as well. The sticky
  // add bar appearing or disappearing changes its size from or to 0.
  observer.disconnect();
  for (const element of [document.body, document.getElementById('MainContent'), footerLine, bar, badge?.firstElementChild]) {
    if (element) observer.observe(element);
  }
  queue();
}

// Only the page itself matters: ignore the scroll events of carousels and other inner scrollers.
document.addEventListener('scroll', (event) => (event.target === document || event.target === pageWrapper) && queue(false), {
  passive: true,
  capture: true,
});
window.addEventListener('resize', () => queue());
window.addEventListener('pageshow', () => queue());
narrow.addEventListener('change', () => queue());

// Theme editor: sections re-render without a page load, so look for the bar and footer line again.
document.addEventListener('shopify:section:load', connect);
document.addEventListener('shopify:section:unload', connect);

// Inbox adds <shopify-chat> after load: pick it up as soon as <body> gets a new child.
new MutationObserver(() => {
  if (!chat) chat = document.querySelector('shopify-chat');
  queue();
}).observe(document.body, { childList: true });

connect();
